import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

// Assets
import purchaseIcon from "../../assets/down-arrow-blue.png";
import sellIcon from "../../assets/up-arrow-green.png";
import stockIcon from "../../assets/stock-icon-2.png";

// Utils
import { formatDate } from "../../utils/toDate";
import { UserAuth } from "../../utils/AuthContext";

// Components
import RoundedTable from "../../components/table-component/RoundedTable";
import PageWrapper from "../../components/PageWrapper";
import InformationBox from "../../components/InformationBox";
import MultipleSearchFilter from "../../components/MultipleSearchFilter";
import BalanceBoxes from "../../components/BalanceBoxes";
import Loader from "../../components/Loader";
import { TABLE_CELL_TYPES } from "../attendance-system/EmployeesAttendanceTable";
import PageHeadline from "../../components/PageHeadline";

const ColorPage = () => {
  const { id } = useParams();

  // HOOKS
  const { t } = useTranslation("general");
  const { userData } = useContext(UserAuth);

  // STATES
  const [loading, setLoading] = useState(false);
  const [colorData, setColorData] = useState([]);
  const [amountInStock, setAmountInStock] = useState(0);
  const [averageCost, setAverageCost] = useState(0);
  const [selectedFilter, setSelectedFilter] = useState({
    title: t("colorStatus"),
    type: "dropdown",
    dropdown: {
      data: [
        { title: t("sell"), value: "SELL" },
        { title: t("purchase"), value: "PURCHASE" },
        { title: t("stock"), value: "STOCK" },
        { title: t("selectAll"), value: "" },
      ],
    },
  });
  const [searchQuery, setSearchQuery] = useState({
    title: t("selectAll"),
    value: "",
  });
  const [selectedOrderStatus, setSelectedOrderStatus] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });

  // STATIC DATA
  const titles = [
    { value: t("Date"), type: TABLE_CELL_TYPES.DATE },
    { value: t("quantity"), type: TABLE_CELL_TYPES.TEXT },
    { value: t("amount"), type: TABLE_CELL_TYPES.MONEY },
    { value: t("colorStatus"), type: TABLE_CELL_TYPES.TEXT },
  ];

  const filterOptions = [
    {
      title: t("colorStatus"),
      type: "dropdown",
      dropdown: {
        data: [
          { title: t("sell"), value: "SELL" },
          { title: t("purchase"), value: "PURCHASE" },
          { title: t("stock"), value: "STOCK" },
          { title: t("selectAll"), value: "" },
        ],
      },
    },
  ];

  // ENDPOINTS

  const getColorData = async (filters = {}) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/getAllFabricColorTransactions/${id}`,
        {
          params: {
            page: pagination.currentPage,
            limit: 10,
            ...(filters.startDate && { startDate: filters.startDate }),
            ...(filters.endDate && { endDate: filters.endDate }),
            ...(filters.orderType && { orderType: filters.orderType.value }),
            organizationId: userData?.organizationId,
          },
          headers: {
            "organization-id": userData?.organizationId,
          },
        }
      );

      const data = response.data;

      if (response.status === 200) {
        if (data.items && data.items.length > 0) {
          const fabricColorInfo = data.items[0].fabricColor;
          setAmountInStock(fabricColorInfo.availableStock);
          setAverageCost(fabricColorInfo.averageCost);
        }

        setPagination({
          currentPage: data.pagination.currentPage,
          totalPages: data.pagination.totalPages,
          totalRecords: data.pagination.totalRecords,
        });

        const formatedData = data.items.map((item) => {
          return [
            {
              value: formatDate(item.date),
              type: TABLE_CELL_TYPES.DATE,
            },
            {
              value: `${item.quantity || 0} ${
                item?.unitOfMeasuring !== null
                  ? t(item.unitOfMeasuring.toLowerCase())
                  : t("meter")
              }`,
              type: TABLE_CELL_TYPES.TEXT,
            },
            {
              value: `${item.price} ${
                item?.currency !== null
                  ? t(item.currency.toLowerCase())
                  : t("usd")
              }`,
              type: TABLE_CELL_TYPES.TEXT,
            },
            {
              value: (
                <div className="u-max-width_100 u-width_full-width u-margin-center o-row o-row--horizontal-align_space-between o-row--vertical-align_center">
                  <p>{t(item.FabricOrderType.toLowerCase())}</p>
                  <img
                    className="u-width-specific_20"
                    src={
                      item.FabricOrderType === "PURCHASE"
                        ? purchaseIcon
                        : item.FabricOrderType === "STOCK"
                        ? stockIcon
                        : sellIcon
                    }
                  />
                </div>
              ),
              type: TABLE_CELL_TYPES.ELEMENT,
            },
          ];
        });

        setColorData(formatedData);
      }
    } catch (error) {
      console.error("Error fetching color data", error);
    } finally {
      setLoading(false);
    }
  };

  // HANDLERS

  const handleSearch = () => {
    const filters = {
      orderType: searchQuery,
    };

    if (startDate) {
      filters.startDate = startDate;
    }
    if (endDate) {
      filters.endDate = endDate;
    }

    setPagination((prev) => ({ ...prev, currentPage: 1 }));

    getColorData(filters);
  };

  // SIDE EFFECTS
  useEffect(() => {
    getColorData();
  }, [selectedOrderStatus, pagination.currentPage]);

  return (
    <PageWrapper withBackButton>
      <div className="u-padding u-padding--bottom_700">
        {/* <h1 className="t-headline-1">{t("")}</h1> */}
        <PageHeadline
          title="colorPage"
          classList="u-margin--top_700 u-margin--bottom_400"
        />

        <div className="u-margin u-margin--bottom_400">
          <MultipleSearchFilter
            options={filterOptions}
            selected={selectedFilter}
            setSelected={setSelectedFilter}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            handleSearch={handleSearch}
          />
        </div>

        {loading && <Loader />}

        {colorData.length && !loading ? (
          <RoundedTable
            titles={titles}
            rows={colorData}
            setPagination={setPagination}
            pagination={pagination}
          />
        ) : !loading ? (
          <InformationBox>{t("noTransactions")}</InformationBox>
        ) : (
          <></>
        )}

        <div>
          <BalanceBoxes
            boxes={[
              {
                title: "amountInStock",
                value: amountInStock + " " + t("meter"),
              },
              {
                title: "averageCost",
                value: averageCost.toFixed(2) + " " + t("USD"),
              },
            ]}
          />
        </div>
      </div>
    </PageWrapper>
  );
};

export default ColorPage;
